import { allQuests, type Condition, type Quest } from '@bratva/content';
import type { Action, SaveDoc } from '@bratva/state';

/**
 * Движок квестов. Условия и шаги описаны в контенте, поэтому здесь нет
 * ни одного «если это квест про ларёк». Проверка идёт по документу сейва:
 * что накоплено, что куплено, с кем какие отношения.
 */

export function conditionMet(condition: Condition, save: SaveDoc): boolean {
  const p = save.player;

  if (condition.stat) return p[condition.stat] >= (condition.gte ?? 0);
  if (condition.npc) return (save.npc[condition.npc] ?? 0) >= (condition.gte ?? 0);
  if (condition.counter) return (save.counters[condition.counter] ?? 0) >= (condition.gte ?? 0);
  if (condition.cash !== undefined) return p.cash >= condition.cash;
  if (condition.level !== undefined) return p.level >= condition.level;
  if (condition.item) return save.owned.includes(condition.item);
  if (condition.vehicle) return save.garage.includes(condition.vehicle);
  if (condition.business) return save.businesses.some((business) => business.def === condition.business);
  if (condition.companion) return save.companions.length > 0;

  // Пустое условие считается выполненным: так квест без входа сразу открыт.
  return true;
}

function unlocked(quest: Quest, save: SaveDoc): boolean {
  return quest.unlock.every((condition) => conditionMet(condition, save));
}

/** Активные квесты с текстом текущего шага — для панели. */
export interface QuestView {
  quest: Quest;
  step: number;
  done: boolean;
}

export function questViews(save: SaveDoc): QuestView[] {
  return allQuests()
    .filter((quest) => save.quests[quest.id] || unlocked(quest, save))
    .map((quest) => {
      const state = save.quests[quest.id];
      return { quest, step: state?.step ?? 0, done: state?.state === 'done' };
    });
}

/**
 * Что должно произойти с квестами после действия игрока: открыться, шагнуть
 * вперёд или закрыться. Возвращает действия, а не меняет состояние само —
 * документ по-прежнему правит только reducer.
 */
export function questActions(save: SaveDoc): Action[] {
  const actions: Action[] = [];

  for (const quest of allQuests()) {
    const state = save.quests[quest.id];
    if (state?.state === 'done') continue;

    if (!state) {
      if (!unlocked(quest, save)) continue;
      const first = quest.steps[0]!;
      actions.push({
        type: 'questStep',
        questId: quest.id,
        title: quest.title,
        step: 0,
        text: first.text,
      });
      continue;
    }

    const step = quest.steps[state.step];
    if (!step || !conditionMet(step.done, save)) continue;

    const nextIndex = state.step + 1;
    const next = quest.steps[nextIndex];
    if (next) {
      actions.push({
        type: 'questStep',
        questId: quest.id,
        title: quest.title,
        step: nextIndex,
        text: next.text,
      });
    } else {
      actions.push({ type: 'questDone', questId: quest.id, title: quest.title, reward: quest.reward });
    }
  }

  return actions;
}
